import type { DataInstance } from '@busy-human/gearbox';
import { Events, ManagementCalendars, ManagementTrainers } from '@busy-human/k9-library';
import { magicNumbers } from './constants';
import { ComputedRef } from 'vue';
import { magicNumbers as consts, commonStrings } from '@/util/constants';

type Props = {
	eventList: DataInstance<Events.Model>[]
	display?: 'week' | 'month' | 'day'
	selectedDate?: Date
	sortCategory?: 'trainer' | 'kennel' | 'time'
};

type AugmentedEvent = {
	event: Events.Model
	startColumn: number
	endColumn: number
	startsBefore: boolean
	endsAfter: boolean
	backgroundColor?: string
	foregroundColor?: string
};

// -- Constants
const DAYLENGTH  = consts.ms.in.day;
const Days   = commonStrings.days;

/** Gets the first date of the selection */
export function getStartDate(props: Props) {
	if (props.selectedDate === undefined ) throw new Error("getStartDate() received undefined props");

	if(props.display === 'month') {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), 1);
	} else if(props.display === 'day') {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), props.selectedDate.getDate());
	} else {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), props.selectedDate.getDate() - props.selectedDate.getDay());
	}
}

/** Gets the last date of the selection */
export function getEndDate(props: Props) {
	if (props.selectedDate === undefined ) throw new Error("getEndDate() received undefined props");

	if(props.display === 'month') {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth() + 1, 1);
	} else if(props.display === 'day') {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), props.selectedDate.getDate() + 1);
	} else {
		return new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth(), (props.selectedDate.getDate() - props.selectedDate.getDay()) + magicNumbers.day.in.week);
	}
}

// https://css-tricks.com/converting-color-spaces-in-javascript/
function getBestForeground(color: string) {
	// Get the RGB value of the color
	const fakeDiv = document.createElement("div");
	fakeDiv.style.color = color;
	document.body.appendChild(fakeDiv);

	const cs = window.getComputedStyle(fakeDiv);
	const pv = cs.getPropertyValue("color");

	document.body.removeChild(fakeDiv);

	// eslint-disable-next-line no-magic-numbers
	const rgb = pv.substring(4).split(")")[0].split(","),
      r = Number(rgb[0]),
      g = Number(rgb[1]),
      b = Number(rgb[2]);

	  // eslint-disable-next-line no-magic-numbers
	  const luminance = ((0.299 * r) + (0.587 * g) + (0.144 * b)) / 255;
	  if(luminance > 0.5) return "#000";
	  else return "#FFF";
}

/** Calculates the columns for events */
// eslint-disable-next-line complexity
export function processEvents(props: Props, evs: Events.Model[], trainers: Map<string, ManagementTrainers.Model>, daysInSelection: ComputedRef<number>): AugmentedEvent[] {
	const currentStart = getStartDate(props);
	const currentEnd   =   getEndDate(props);
	
	const cStartN = currentStart.getTime();
	const cEndN   =   currentEnd.getTime();
	
	const arr: AugmentedEvent[] = [];

	// Loop through given events
	for(const ev of evs) {

		const eventStart = ev.startTime.includes('T') ? new Date(ev.startTime) : new Date(`${ev.startTime}T00:00:00`);
		const tmpEnd =     ev.endTime.includes('T')   ? new Date(ev.endTime)   : new Date(`${ev.endTime  }T00:00:00`);
		const eventEnd =   ev.endTime.includes('T')   ? tmpEnd                 : new Date(tmpEnd.getFullYear(), tmpEnd.getMonth(), tmpEnd.getDate());
		const eStartN =    eventStart.getTime();
		const eEndN =        eventEnd.getTime();
		const trainer = ('trainerId' in ev && ev.trainerId) ? trainers.get(ev.trainerId) : undefined;

		const tmp: AugmentedEvent = {
			event: ev,
			startColumn: 1,
			startsBefore: false,
			endColumn: daysInSelection.value,
			endsAfter: false,
			backgroundColor: trainer?.calendarColor || 'darkgrey',
			foregroundColor: getBestForeground(trainer?.calendarColor || "darkgrey")
		};

		// Ignore events that are outside of the selection range
		if(!(eStartN < cEndN && cStartN < eEndN)) continue;

		// If the event starts before the selection, mark it
		if(eStartN < cStartN) {
			tmp.startsBefore = true;
		} else if(props.display === 'month') {
				tmp.startColumn = eventStart.getDate();
			}
			else if(props.display === 'week') {
				tmp.startColumn = 1 + Math.floor((eStartN - cStartN) / DAYLENGTH);
			}

		// If the event ends after the selection, mark it
		if(eEndN > cEndN) {
			tmp.endsAfter = true;
		} else if(eEndN === cEndN) {
			if(props.display === 'month') {
				if(props.selectedDate === undefined ) throw new Error("Props are undefined in processEvents function :'( ");

				tmp.endColumn = new Date(props.selectedDate.getFullYear(), props.selectedDate.getMonth() + 1, 0).getDate();
			} else if(props.display === 'week') {
				tmp.endColumn = Math.floor((eEndN - cStartN) / DAYLENGTH);
			}
		} else if(props.display === 'month') {
				tmp.endColumn = eventEnd.getDate();
			} else if(props.display === 'week') {
				tmp.endColumn = Math.floor((eEndN - cStartN) / DAYLENGTH);
			}
		if(tmp.endColumn !== tmp.startColumn) {
			tmp.endColumn += 1;
		}
		arr.push(tmp);
	}
	return arr;
}

/** Returns true if the event is an all day event, aka should be hidden in time view */
export function isAllDayEvent(event: Events.Model) {
	return !event.startTime.includes("T");
}

/** Formats a number into the month/day offset */
export function transformDateNumber(i: number, props: Props) {
	i -= 1;
	const d = getStartDate(props);
	// console.log("TRANSFORMDATENU<BER", i ,new Date(d.getFullYear(), d.getMonth(), d.getDate() + i).getDate());
	return new Date(d.getFullYear(), d.getMonth(), d.getDate() + i).getDate();
}

/** Formats a number into its day name */
export function transformDateDay(i: number, props: Props) {
	i -= 1;
	const d = getStartDate(props);
	return Days[new Date(d.getFullYear(), d.getMonth(), d.getDate() + i).getDay()];
}

export function transformDateToTime(what:any) {
	console.log("WHAT::",new Date (what.event.startTime));
}

const n = {
	'0'  :   0,
	'2'  :   2,
	'3'  :   3,
	'4'  :   4,
	'6'  :   6,
	'16' :  16,
	'186': 186,
	'255': 255,
	r    :   0.299,
	g    :   0.587,
	b    :   0.114,
};

export function padZero(str: string, len = 2) {
    const zeros = new Array(len).join('0');
    return (zeros + str).slice(-len);
};

// https://stackoverflow.com/a/35970186
export function invertColor(hex: string, bw = false) {
    if (hex.indexOf('#') === 0) {
        hex = hex.slice(1);
    }
    // convert 3-digit hex to 6-digits.
    if (hex.length === n['3']) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    if (hex.length !== n['6']) {
        return null;
    }
    const r = parseInt(hex.slice(n['0'], n['2']), n['16']),
          g = parseInt(hex.slice(n['2'], n['4']), n['16']),
          b = parseInt(hex.slice(n['4'], n['6']), n['16']);
    if (bw) {
        // https://stackoverflow.com/a/3943023/112731
        return ((r * n.r) + (g * n.g) + (b * n.b)) > n['186']
            ? '#000000'
            : '#FFFFFF';
    }
    // invert color components
    const rr = (n['255'] - r).toString(n['16']),
          gr = (n['255'] - g).toString(n['16']),
          br = (n['255'] - b).toString(n['16']);
    // pad each with zeros and return
    return `#${padZero(rr)}${padZero(gr)}${padZero(br)}`;
}

/** Builds a CSS style object based on an event */
export function buildEventCSS(ev: AugmentedEvent) {
	return {
		'--column': ev.startColumn,
		'--end-column': ev.endColumn,
		'--bg-color': ev.backgroundColor || 'darkgrey',
		'--fg-color': invertColor(ev.backgroundColor || 'darkgrey', true) || 'black'
	};
}

/** Builds an empty placeholder event for display purposes */
export function buildEmptyCSS(cal: ManagementCalendars.Model) {
	return {
		'--outline-color': cal.backgroundColor
	};
}

export const fns = {
	getStartDate,
	getEndDate,
	processEvents,
	isAllDayEvent,
	transformDateNumber,
	transformDateDay,
	transformDateToTime,
	buildEventCSS,
	buildEmptyCSS,
	invertColor,
	padZero,
};